import React, { useEffect, useMemo, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { generateManutenzionePdfBlob, generateVitPdfBlob } from '../pdf/clientPdf';
import { useNavigate } from 'react-router-dom';
import ClientAlarmPopup from '../components/ClientAlarmPopup';
import StoricoLetture from './StoricoLetture.jsx';
import WhatsAppFloatingButton from '../components/WhatsAppFloatingButton';
import { Badge, Card } from '../components/ui';

const API_BASE = '/api';
const WHATSAPP_BUSINESS_NUMBER = '393519604932';

const SHOW_SMART_SYSTEMS = true;


const CONTACT_AVAILABILITY_MODES = [
  { value: 'always', label: 'Sempre reperibile' },
  { value: 'business_hours', label: 'Solo orario lavorativo' },
];

const CLIENT_ALARM_STATUS_FILTERS = [
  { value: 'all', label: 'Tutti' },
  { value: 'active', label: 'Attivi' },
  { value: 'acknowledged', label: 'Presi in carico' },
  { value: 'resolved', label: 'Risolti' },
];

const CLIENT_VOICE_FILTERS = [
  { value: 'all', label: 'Tutte chiamate' },
  { value: 'none', label: 'Non avviate' },
  { value: 'in_progress', label: 'In corso' },
  { value: 'problems', label: 'Problemi' },
];

const WEEK_DAYS = [
  { key: 'monday', label: 'Lunedì' },
  { key: 'tuesday', label: 'Martedì' },
  { key: 'wednesday', label: 'Mercoledì' },
  { key: 'thursday', label: 'Giovedì' },
  { key: 'friday', label: 'Venerdì' },
  { key: 'saturday', label: 'Sabato' },
  { key: 'sunday', label: 'Domenica' },
];

const DEFAULT_WORKING_HOURS = {
  timezone: 'Europe/Rome',
  monday: { enabled: true, from: '08:00', to: '18:00' },
  tuesday: { enabled: true, from: '08:00', to: '18:00' },
  wednesday: { enabled: true, from: '08:00', to: '18:00' },
  thursday: { enabled: true, from: '08:00', to: '18:00' },
  friday: { enabled: true, from: '08:00', to: '18:00' },
  saturday: { enabled: false, from: '08:00', to: '13:00' },
  sunday: { enabled: false, from: '08:00', to: '13:00' },
};

function normalizeWorkingHoursForForm(workingHours = {}) {
  const result = {
    ...DEFAULT_WORKING_HOURS,
    ...(workingHours || {}),
    timezone: workingHours?.timezone || 'Europe/Rome',
  };

  for (const day of WEEK_DAYS) {
    result[day.key] = {
      ...DEFAULT_WORKING_HOURS[day.key],
      ...(workingHours?.[day.key] || {}),
    };
  }

  return result;
}






function normalizeVoiceStatus(status = '') {
  return String(status || '').trim().toLowerCase().replace(/-/g, '_');
}

function getVoiceStatusLabel(status = '') {
  const labels = {
    scheduled: 'Schedulata',
    pending: 'In preparazione',
    initiated: 'Avviata',
    ringing: 'Squilla',
    answered: 'Risposta',
    completed: 'Completata',
    acknowledged: 'Presa in carico',
    no_answer: 'Nessuna risposta',
    busy: 'Occupato',
    failed: 'Fallita',
    canceled: 'Annullata',
    invalid_digit: 'Tasto non valido',
    escalation_failed: 'Tentativi esauriti',
    skipped: 'Saltata',
    unknown: 'Sconosciuta',
  };

  const normalized = normalizeVoiceStatus(status);
  return labels[normalized] || status || 'Non avviata';
}

function getAlarmStatusLabel(status = '') {
  const labels = {
    active: 'Attivo',
    acknowledged: 'Preso in carico',
    resolved: 'Risolto',
  };

  return labels[String(status || '').toLowerCase()] || status || '-';
}

function getAlarmStatusClass(status = '') {
  const s = String(status || '').toLowerCase();

  if (s === 'active') {
    return 'bg-red-100 text-red-700';
  }

  if (s === 'acknowledged') {
    return 'bg-amber-100 text-amber-700';
  }

  if (s === 'resolved') {
    return 'bg-emerald-100 text-emerald-700';
  }

  return 'bg-slate-100 text-slate-700';
}

function getVoiceStatusClass(status = '') {
  const s = normalizeVoiceStatus(status);

  if (s === 'acknowledged') {
    return 'bg-emerald-100 text-emerald-700';
  }

  if (['scheduled', 'pending', 'initiated', 'ringing', 'answered'].includes(s)) {
    return 'bg-sky-100 text-sky-700';
  }

  if (
    [
      'failed',
      'busy',
      'no_answer',
      'canceled',
      'invalid_digit',
      'escalation_failed',
      'skipped',
    ].includes(s)
  ) {
    return 'bg-orange-100 text-orange-700';
  }

  return 'bg-slate-100 text-slate-700';
}

function getSortedVoiceAttempts(event = {}) {
  return Array.isArray(event.voice_attempts)
    ? event.voice_attempts.slice().reverse()
    : [];
}

function getVoiceAttemptCount(event = {}) {
  const attemptsFromCounter = Number(event.voice_call_attempts || 0);

  if (attemptsFromCounter > 0) {
    return attemptsFromCounter;
  }

  return Array.isArray(event.voice_attempts) ? event.voice_attempts.length : 0;
}

function eventMatchesClientVoiceFilter(event = {}, filter = 'all') {
  if (filter === 'all') return true;

  const status = normalizeVoiceStatus(event.voice_call_status);
  const escalationStatus = normalizeVoiceStatus(event.voice_escalation_status);
  const attempts = getVoiceAttemptCount(event);

  if (filter === 'none') {
    return !status && attempts === 0 && event.voice_acknowledged !== true;
  }

  if (filter === 'scheduled') {
    return ['scheduled', 'pending'].includes(status);
  }

  if (filter === 'in_progress') {
    return ['initiated', 'ringing', 'answered'].includes(status);
  }

  if (filter === 'acknowledged') {
    return event.voice_acknowledged === true || status === 'acknowledged';
  }

  if (filter === 'problems') {
    return (
      escalationStatus.includes('rate_limit') ||
      [
        'completed',
        'no_answer',
        'busy',
        'failed',
        'canceled',
        'skipped',
        'escalation_failed',
        'invalid_digit',
      ].includes(status)
    );
  }

  return true;
}


function normalizeClientSensorId(sensorId = '') {
  const raw = String(sensorId || '').trim();

  // TEMP-1 / TEMP-01 -> TEMP-01, e lo stesso per HUM-* (umidita' delle sonde
  // XY-MD04 sul bus del Pulse).
  const match = raw.match(/^(TEMP|HUM)-0?(\d+)$/i);

  if (match) {
    const number = Number(match[2]);

    if (Number.isFinite(number) && number > 0) {
      return `${match[1].toUpperCase()}-${String(number).padStart(2, '0')}`;
    }
  }

  return raw;
}

// Prima le temperature (PT100 TEMP-01..06, poi quelle del bus), poi le umidita'.
function sensorSortKey(sensorId = '') {
  const id = String(sensorId || '');
  const number = Number(id.replace(/\D/g, '')) || 0;
  const group = id.startsWith('TEMP-') ? 0 : id.startsWith('HUM-') ? 1 : 2;

  return group * 1000 + number;
}

function getDeviceChannels(device = {}) {
  /*
    /api/iot/client/systems manda `channels` per device; le risposte vecchie
    avevano un solo `channel` per voce, che resta accettato.
  */
  const configuredChannels =
    Array.isArray(device?.channels) && device.channels.length
      ? device.channels
      : Array.isArray(device?.sensors) && device.sensors.length
      ? device.sensors
      : device?.channel
      ? [device.channel]
      : [];

  const lastMeasures = Array.isArray(device?.last_measures)
    ? device.last_measures
    : [];

  const channelMap = new Map();

  configuredChannels.forEach((channel, index) => {
    if (!channel || channel.enabled === false) return;

    const sensorId = normalizeClientSensorId(
      channel.sensor_id ||
        channel.sensorId ||
        channel.id ||
        `CHANNEL-${index + 1}`
    );

    if (!sensorId) return;

    channelMap.set(sensorId, {
      ...channel,
      sensor_id: sensorId,
    });
  });

  lastMeasures.forEach((measure, index) => {
    if (!measure) return;

    const sensorId = normalizeClientSensorId(
      measure.sensor_id ||
        measure.sensorId ||
        measure.id ||
        `MEASURE-${index + 1}`
    );

    if (!sensorId || !/^(TEMP|HUM)-\d+$/i.test(sensorId)) {
      return;
    }

    const configuredChannel = channelMap.get(sensorId) || {};

    channelMap.set(sensorId, {
      ...configuredChannel,
      ...measure,

      sensor_id: sensorId,

      label:
        configuredChannel.label ||
        measure.sensor_label ||
        measure.label ||
        sensorId,

      last_value:
        measure.value ??
        measure.last_value ??
        configuredChannel.last_value ??
        configuredChannel.value ??
        null,

      value:
        measure.value ??
        measure.last_value ??
        configuredChannel.value ??
        configuredChannel.last_value ??
        null,

      unit:
        measure.unit ||
        configuredChannel.unit ||
        (sensorId.startsWith('HUM-') ? '%RH' : '°C'),
    });
  });

  return Array.from(channelMap.values()).sort(
    (a, b) => sensorSortKey(a.sensor_id) - sensorSortKey(b.sensor_id)
  );
}


export default function DashboardCliente() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [machines, setMachines] = useState([]);
  const [clientSystems, setClientSystems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [message, setMessage] = useState('');
  const [workingHoursForm, setWorkingHoursForm] = useState(DEFAULT_WORKING_HOURS);
  const [savingWorkingHours, setSavingWorkingHours] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const [expandedMachineId, setExpandedMachineId] = useState(null);
  const [error, setError] = useState('');
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  const [q, setQ] = useState('');
  const [statusMode, setStatusMode] = useState('all');
  const [activeArea, setActiveArea] = useState('works');
  const [alarmContactForm, setAlarmContactForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    role: 'reperibile',
    availabilityMode: 'always',
    voiceEnabled: true,
    notes: '',
  });

  const [editingAlarmContactId, setEditingAlarmContactId] = useState(null);
  const [savingAlarmContact, setSavingAlarmContact] = useState(false);

  const [alarmEvents, setAlarmEvents] = useState([]);
  const [loadingAlarmEvents, setLoadingAlarmEvents] = useState(false);
  const [ackingAlarmId, setAckingAlarmId] = useState(null);

  const [alarmStatusFilter, setAlarmStatusFilter] = useState('all');
  const [alarmVoiceFilter, setAlarmVoiceFilter] = useState('all');
  const [alarmQuery, setAlarmQuery] = useState('');

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('role');
    localStorage.removeItem('profilePicture');
    localStorage.removeItem('mustChangePassword');
    navigate('/login');
  };

  const loadWorks = async () => {
    const url = `${API_BASE}/work/mine/full?status=${encodeURIComponent(statusMode)}`;
    const res = await authFetch(url);

    if (!res.ok) {
      if (res.status === 401) {
        logout();
        return [];
      }
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.error || 'Errore nel recupero lavorazioni');
    }

    const data = await res.json();
    return Array.isArray(data) ? data : [];
  };

  const loadMachines = async () => {
    try {
      const res = await authFetch(`${API_BASE}/machines/mine`);
      if (!res.ok) {
        return [];
      }
      const data = await res.json().catch(() => []);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  };

  const loadDepartments = async () => {
    try {
      const res = await authFetch(`${API_BASE}/departments/mine`);

      if (!res.ok) {
        return [];
      }

      const data = await res.json().catch(() => []);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  };

  const loadAlarmEvents = async () => {
    try {
      setLoadingAlarmEvents(true);

      const res = await authFetch(`${API_BASE}/alarms/events?status=all`);

      if (!res.ok) {
        return [];
      }

      const data = await res.json().catch(() => null);
      return Array.isArray(data?.events) ? data.events : [];
    } catch {
      return [];
    } finally {
      setLoadingAlarmEvents(false);
    }
  };

  const loadClientSystems = async () => {
    try {
      const res = await authFetch(`${API_BASE}/iot/client/systems`);

      if (!res.ok) {
        return [];
      }

      const data = await res.json().catch(() => null);
      return Array.isArray(data?.departments) ? data.departments : [];
    } catch {
      return [];
    }
  };

  const loadAll = async () => {
    setIsLoading(true);
    setError('');

    try {
      const [worksData, machinesData, systemsData, departmentsData, alarmEventsData] = await Promise.all([
        loadWorks(),
        loadMachines(),
        SHOW_SMART_SYSTEMS ? loadClientSystems() : Promise.resolve([]),
        loadDepartments(),
        loadAlarmEvents(),
      ]);

    setRows(worksData);
    setMachines(machinesData);
    setClientSystems(systemsData);
    setDepartments(departmentsData);

    setAlarmEvents(alarmEventsData);

    if (!selectedDepartmentId) {
      const firstDepartmentId =
        departmentsData[0]?._id ||
        systemsData[0]?.department?._id ||
        '';

      if (firstDepartmentId) {
        setSelectedDepartmentId(firstDepartmentId);
      }
    }
    } catch (e) {
      setError(e?.message || 'Errore nella richiesta');
      setRows([]);
      setMachines([]);
      setClientSystems([]);
      setDepartments([]);
      setAlarmEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusMode]);

  const filteredRows = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((r) => {
      const w = r?.workOrder || {};
      const hay = [
        w.internalCode,
        w.externalCode,
        w.companyName,
        w.place,
        w.description,
        w.type,
        w.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(query);
    });
  }, [rows, q]);

  const stats = useMemo(() => {
    const closedWorks = rows.filter((r) => {
      const s = r?.workOrder?.status;
      return s === 'chiusa' || s === 'completata';
    }).length;

    const reportsCount = rows.reduce((acc, r) => acc + (r?.reports?.length || 0), 0);

    return {
      closedWorks,
      reportsCount,
      machinesCount: machines.length,
      smartCount: machines.filter((m) => Array.isArray(m.devices) && m.devices.length > 0).length,
    };
  }, [rows, machines]);

  const selectedDepartment = useMemo(() => {
    if (!clientSystems.length) return null;

    return (
      clientSystems.find((item) => {
        const id = item?.department?._id || 'senza-reparto';
        return id === selectedDepartmentId;
      }) || clientSystems[0]
    );
  }, [clientSystems, selectedDepartmentId]);

  const selectedDepartmentMachines = useMemo(() => {
    return Array.isArray(selectedDepartment?.machines)
      ? selectedDepartment.machines
      : [];
  }, [selectedDepartment]);

  const smartMachinesForDisplay = useMemo(() => {
    const machineMap = new Map();

    const getMachineKey = (machine = {}, fallback = '') => {
      return String(
        machine?._id ||
          machine?.id ||
          machine?.matricola ||
          machine?.serialNumber ||
          fallback
      );
    };

    const getDeviceKey = (device = {}, fallback = '') => {
      return String(
        device?.device_id ||
          device?.deviceId ||
          device?._id ||
          device?.id ||
          fallback
      );
    };

    const normalizeDeviceForMachine = (device = {}, machine = {}, department = null) => {
      const deviceId =
        device?.device_id ||
        device?.deviceId ||
        device?._id ||
        device?.id ||
        '';

    const channels = getDeviceChannels(device).map((channel) => ({
      ...channel,

      machineRef:
        channel?.machineRef ||
        machine ||
        null,

      departmentRef:
        channel?.departmentRef ||
        channel?.machineRef?.departmentRef ||
        machine?.departmentRef ||
        department ||
        null,
    }));

      return {
        ...device,
        device_id: String(deviceId || ''),
        machineRef: device?.machineRef || machine || null,
        departmentRef: device?.departmentRef || machine?.departmentRef || department || null,
        channels,
      };
    };

    const upsertMachine = (machine = {}, department = null, fallback = '') => {
      if (!machine || typeof machine !== 'object') return;

      const machineKey = getMachineKey(machine, fallback);
      if (!machineKey) return;

      const existing = machineMap.get(machineKey) || {};
      const existingDevices = Array.isArray(existing.devices) ? existing.devices : [];
      const incomingDevices = Array.isArray(machine.devices) ? machine.devices : [];

      const deviceMap = new Map();

      existingDevices.forEach((device, index) => {
        const key = getDeviceKey(device, `existing-${index}`);
        deviceMap.set(key, device);
      });

      incomingDevices.forEach((device, index) => {
        const normalized = normalizeDeviceForMachine(device, machine, department);
        const key = getDeviceKey(normalized, `incoming-${index}`);
        const previous = deviceMap.get(key) || {};

        const previousChannels = Array.isArray(previous.channels) ? previous.channels : [];
        const incomingChannels = Array.isArray(normalized.channels) ? normalized.channels : [];

        deviceMap.set(key, {
          ...previous,
          ...normalized,
          channels: incomingChannels.length ? incomingChannels : previousChannels,
        });
      });

      machineMap.set(machineKey, {
        ...existing,
        ...machine,
        departmentRef: machine?.departmentRef || department || existing?.departmentRef || null,
        devices: Array.from(deviceMap.values()).filter((device) => device?.device_id),
      });
    };

    machines.forEach((machine, index) => {
      upsertMachine(machine, machine?.departmentRef || null, `machine-${index}`);
    });

    clientSystems.forEach((system, systemIndex) => {
      const department = system?.department || null;
      const systemMachines = Array.isArray(system?.machines) ? system.machines : [];

      systemMachines.forEach((machine, machineIndex) => {
        upsertMachine(
          machine,
          department,
          `system-${systemIndex}-machine-${machineIndex}`
        );
      });
    });

    return Array.from(machineMap.values());
  }, [machines, clientSystems]);

  const hasClientMachines = smartMachinesForDisplay.length > 0;
  const canShowSmartBoxes = SHOW_SMART_SYSTEMS && hasClientMachines;

  const smartDevicesForHistory = useMemo(() => {
    const deviceMap = new Map();

    smartMachinesForDisplay.forEach((machine) => {
      const devices = Array.isArray(machine.devices) ? machine.devices : [];

      devices.forEach((device, index) => {
        const deviceId =
          device?.device_id ||
          device?.deviceId ||
          device?._id ||
          device?.id ||
          '';

        if (!deviceId) return;

        const key = String(deviceId);
        const previous = deviceMap.get(key) || {};

        const previousChannels = Array.isArray(previous.channels)
          ? previous.channels
          : [];
        
        const incomingChannels = getDeviceChannels(device); 

        deviceMap.set(key, {
          ...previous,
          ...device,
          device_id: key,
          machineRef: device?.machineRef || machine,
          departmentRef: device?.departmentRef || machine?.departmentRef || null,
          channels: incomingChannels.length ? incomingChannels : previousChannels,
        });
      });
    });

    return Array.from(deviceMap.values());
  }, [smartMachinesForDisplay]);

  const selectedDepartmentInfo = useMemo(() => {
  const fromDepartments = departments.find(
    (department) => String(department._id) === String(selectedDepartmentId)
    );

    if (fromDepartments) return fromDepartments;

    return selectedDepartment?.department || departments[0] || null;
  }, [departments, selectedDepartment, selectedDepartmentId]);

  useEffect(() => {
  setWorkingHoursForm(
    normalizeWorkingHoursForForm(selectedDepartmentInfo?.workingHours || {})
    );
  }, [selectedDepartmentInfo?._id]);

  const selectedAlarmContacts = useMemo(() => {
    return Array.isArray(selectedDepartmentInfo?.alarmContacts)
      ? selectedDepartmentInfo.alarmContacts.filter((contact) => contact.isActive !== false)
      : [];
  }, [selectedDepartmentInfo]);

  const resetAlarmContactForm = () => {
    setAlarmContactForm({
      firstName: '',
      lastName: '',
      phone: '',
      role: 'reperibile',
      availabilityMode: 'always',
      voiceEnabled: true,
      notes: '',
    });
    setEditingAlarmContactId(null);
  };

  const handleAlarmContactChange = (e) => {
    const { name, value, type, checked } = e.target;

    setAlarmContactForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const applyUpdatedDepartment = (updatedDepartmentPayload) => {
    const updatedDepartment =
      updatedDepartmentPayload?.department || updatedDepartmentPayload;

    if (!updatedDepartment?._id) return;

    setDepartments((prev) =>
      prev.map((department) =>
        String(department._id) === String(updatedDepartment._id)
          ? {
              ...department,
              ...updatedDepartment,
            }
          : department
      )
    );

    setClientSystems((prev) =>
      prev.map((item) => {
        const currentDepartmentId = item?.department?._id;

        if (String(currentDepartmentId) !== String(updatedDepartment._id)) {
          return item;
        }

        return {
          ...item,
          department: {
            ...item.department,
            ...updatedDepartment,
          },
        };
      })
    );
  };

  const handleSubmitAlarmContact = async (e) => {
    e.preventDefault();

    if (!selectedDepartmentInfo?._id) {
      setError('Seleziona un reparto valido.');
      return;
    }

    if (!alarmContactForm.firstName.trim()) {
      setError('Il nome del referente è obbligatorio.');
      return;
    }

    if (!alarmContactForm.lastName.trim()) {
      setError('Il cognome del referente è obbligatorio.');
      return;
    }

    if (!alarmContactForm.phone.trim()) {
      setError('Il numero di telefono è obbligatorio.');
      return;
    }

    try {
      setSavingAlarmContact(true);
      setError('');

      const isEdit = Boolean(editingAlarmContactId);

      const url = isEdit
        ? `${API_BASE}/departments/${selectedDepartmentInfo._id}/alarm-contacts/${editingAlarmContactId}`
        : `${API_BASE}/departments/${selectedDepartmentInfo._id}/alarm-contacts`;

      const method = isEdit ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(alarmContactForm),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.message || 'Errore salvataggio contatto allarme');
      }

      applyUpdatedDepartment(data);
      resetAlarmContactForm();
    } catch (err) {
      setError(err?.message || 'Impossibile salvare il contatto allarme.');
    } finally {
      setSavingAlarmContact(false);
    }
  };

  const handleEditAlarmContact = (contact) => {
    setEditingAlarmContactId(contact._id);

    setAlarmContactForm({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      phone: contact.phone || '',
      role: contact.role || 'reperibile',
      availabilityMode: contact.availabilityMode || 'always',
      voiceEnabled: contact.voiceEnabled !== false,
      notes: contact.notes || '',
    });
  };

  const handleDeleteAlarmContact = async (contact) => {
    const confirmed = window.confirm(
      `Vuoi disattivare il contatto ${contact.firstName || ''} ${contact.lastName || ''}?`
    );

    if (!confirmed) return;

    try {
      setSavingAlarmContact(true);
      setError('');

      const res = await authFetch(
        `${API_BASE}/departments/${selectedDepartmentInfo._id}/alarm-contacts/${contact._id}`,
        {
          method: 'DELETE',
        }
      );

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.message || 'Errore disattivazione contatto');
      }

      applyUpdatedDepartment(data);
      resetAlarmContactForm();
    } catch (err) {
      setError(err?.message || 'Impossibile disattivare il contatto.');
    } finally {
      setSavingAlarmContact(false);
    }
  };

  const handleWorkingHoursChange = (dayKey, field, value) => {
    setWorkingHoursForm((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: field === 'enabled' ? Boolean(value) : value,
      },
    }));
  };

  const handleWorkingHoursTimezoneChange = (value) => {
    setWorkingHoursForm((prev) => ({
      ...prev,
      timezone: value,
    }));
  };

  const handleSubmitWorkingHours = async () => {
    if (!selectedDepartmentInfo?._id) {
      setError('Seleziona un reparto valido.');
      return;
    }

    try {
      setSavingWorkingHours(true);
      setError('');
      setMessage('');

      const res = await authFetch(
        `${API_BASE}/departments/${selectedDepartmentInfo._id}/alarm-settings`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            workingHours: workingHoursForm,
          }),
        }
      );

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.message || 'Errore salvataggio orario lavorativo');
      }

      applyUpdatedDepartment(data);
      setMessage('Orario lavorativo aggiornato.');
    } catch (err) {
      setError(err?.message || 'Impossibile salvare l’orario lavorativo.');
    } finally {
      setSavingWorkingHours(false);
    }
  };

  const getContactRoleLabel = () => {
    return 'Responsabile';
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const formatDateTime = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return '-';
    }
  };

  const renderReportsBadge = (reports = []) => {
    if (!reports.length) return <span className="text-gray-400">—</span>;

    const types = Array.from(
      new Set(reports.map((r) => (r?.reportType || '').toLowerCase()).filter(Boolean))
    );

    const label = types
      .map((t) => (t === 'vit' ? 'VIT' : t === 'manutenzione' ? 'Manutenzione' : t))
      .join(', ');

    return (
      <Badge className="bg-sky-100 text-sky-700">
        {label}
      </Badge>
    );
  };

  const renderStatusBadge = (status) => {
    const s = String(status || '').toLowerCase();

    if (s === 'chiusa' || s === 'completata') {
      return (
        <Badge className="bg-emerald-100 text-emerald-700">
          {status}
        </Badge>
      );
    }

    if (s === 'aperta' || s === 'in corso' || s === 'in_corso') {
      return (
        <Badge className="bg-amber-100 text-amber-700">
          {status}
        </Badge>
      );
    }

    return (
      <Badge className="bg-gray-100 text-gray-700">
        {status || '-'}
      </Badge>
    );
  };

  const renderSmartBadge = (devices = []) => {
    if (!devices.length) {
      return (
        <Badge className="bg-gray-100 text-gray-600">
          Nessun device
        </Badge>
      );
    }

    const latestSeen = devices[0]?.last_seen_at ? new Date(devices[0].last_seen_at).getTime() : 0;
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    const isRecent = latestSeen && now - latestSeen < oneDay;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${
          isRecent
            ? 'bg-emerald-100 text-emerald-700'
            : 'bg-amber-100 text-amber-700'
        }`}
      >
        {isRecent ? 'Smart attivo' : 'Smart da verificare'}
      </span>
    );
  };

  const getDeviceDisplayId = (device = {}) => {
    return (
      device?.device_id ||
      device?.deviceId ||
      device?._id ||
      device?.id ||
      'Device'
    );
  };



  const getChannelLabel = (channel = {}) => {
    return (
      channel?.label ||
      channel?.name ||
      channel?.sensor_label ||
      channel?.sensor_id ||
      channel?.id ||
      'Sonda'
    );
  };

  const getChannelValueLabel = (channel = {}) => {
    const value =
      channel?.last_value ??
      channel?.latest_value ??
      channel?.value ??
      channel?.lastReading?.value ??
      channel?.last_reading?.value ??
      channel?.temperature ??
      null;

    if (value === null || value === undefined || value === '') {
      return 'N/D';
    }

    const rawUnit =
      channel?.unit ||
      channel?.unita ||
      channel?.measure_unit ||
      channel?.lastReading?.unit ||
      channel?.last_reading?.unit ||
      '°C';

    // Il firmware del Pulse scrive "C" per i gradi.
    const unit = rawUnit === 'C' ? '°C' : rawUnit;

    const numeric = Number(value);
    const formattedValue = Number.isFinite(numeric)
      ? numeric.toFixed(2).replace(/\.00$/, '')
      : String(value);

    return `${formattedValue} ${unit}`;
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'documento.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleRegeneratePdf = async (workOrderId) => {
    setPdfLoadingId(workOrderId);
    try {
      const repRes = await authFetch(`${API_BASE}/work/${workOrderId}/report`);
      if (!repRes.ok) {
        const err = await repRes.json().catch(() => ({}));
        alert(err?.error || 'Errore nel recupero report');
        return;
      }

      const { workOrder, report } = await repRes.json();

      const reportType = String(report?.reportType || '').toLowerCase();
      const isVit =
        reportType === 'vit' || reportType.includes('vit') || reportType.includes('verifica');
      const isMan =
        reportType === 'manutenzione' || reportType.includes('manut');

      if (!isVit && !isMan) {
        alert(`Report non riconosciuto: "${report?.reportType}"`);
        return;
      }

      const template = report?.template || 'CIESSE_IMPIANTI';
      const formData = report?.formData || {};
      const vitFormData = isVit
        ? {
            ...formData,
            codiceInterno:
              workOrder?.internalCode ||
              formData?.codiceInterno ||
              '',
          }
        : formData;
      const checks = report?.checks || {};
      const tecnicoSignature = report?.tecnicoSignature || null;
      const clienteSignature = report?.clienteSignature || null;

      // Ristampa di un verbale gia archiviato: si rigenera in locale e
      // NON si notifica nessuno (inviaReport resta false).
      const blob = isVit
        ? await generateVitPdfBlob({
            template,
            formData: vitFormData,
            tecnicoSignature,
            clienteSignature,
          })
        : await generateManutenzionePdfBlob({
            template,
            formData,
            checks,
            tecnicoSignature,
            clienteSignature,
          });

      const vitCode =
        workOrder?.internalCode ||
        'SENZA-CODICE';

      const maintenanceCode =
        workOrder?.internalCode ||
        workOrder?.externalCode ||
        'SENZA-CODICE';

      const filename = isVit
        ? `verbale_intervento_tecnico_${vitCode}.pdf`
        : `verbale_manutenzione_${maintenanceCode}.pdf`;

      downloadBlob(blob, filename);
    } catch {
      alert('Errore di rete durante la rigenerazione PDF');
    } finally {
      setPdfLoadingId(null);
    }
  };

  const filteredAlarmEvents = useMemo(() => {
    const query = alarmQuery.trim().toLowerCase();

    return alarmEvents.filter((event) => {
      const status = String(event.status || '').toLowerCase();

      if (alarmStatusFilter !== 'all' && status !== alarmStatusFilter) {
        return false;
      }

      if (!eventMatchesClientVoiceFilter(event, alarmVoiceFilter)) {
        return false;
      }

      if (!query) {
        return true;
      }

      const attemptsText = Array.isArray(event.voice_attempts)
        ? event.voice_attempts
            .map((attempt) =>
              [
                attempt.contact_name,
                attempt.phone,
                attempt.status,
                attempt.error,
              ]
                .filter(Boolean)
                .join(' ')
            )
            .join(' ')
        : '';

      const haystack = [
        event.sensor_label,
        event.sensor_id,
        event.device_id,
        event.machineRef?.nome,
        event.machineRef?.matricola,
        event.departmentRef?.name,
        event.departmentRef?.sede,
        event.value,
        event.unit,
        event.voice_call_to,
        event.voice_last_error,
        attemptsText,
      ]
        .filter((value) => value !== null && value !== undefined && value !== '')
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [alarmEvents, alarmStatusFilter, alarmVoiceFilter, alarmQuery]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-lg animate-pulse">
          Caricamento area cliente...
        </div>
      </div>
    );
  }



const handleAcknowledgeAlarm = async (event) => {
  if (!event?._id) return;

  const confirmed = window.confirm(
    'Vuoi prendere in carico questo allarme? Le chiamate automatiche successive verranno fermate.'
  );

  if (!confirmed) return;

  try {
    setAckingAlarmId(event._id);
    setError('');
    setMessage('');

    const res = await authFetch(`${API_BASE}/alarms/events/${event._id}/ack`, {
      method: 'POST',
    });

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      throw new Error(data?.message || 'Errore presa in carico allarme');
    }

    setMessage('Allarme preso in carico correttamente.');

    const refreshed = await loadAlarmEvents();
    setAlarmEvents(refreshed);
  } catch (err) {
    setError(err?.message || 'Impossibile prendere in carico l’allarme.');
  } finally {
    setAckingAlarmId(null);
  }
};




  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-gray-100 to-white pb-12">
      <ClientAlarmPopup />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 pt-6 md:pt-10 space-y-5 md:space-y-6">
        

{/*
<section className="relative overflow-hidden rounded-3xl shadow-xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_20%,#38bdf8,transparent_40%),radial-gradient(circle_at_80%_0%,#22c55e,transparent_40%)]" />

  <div className="relative p-6 md:p-8">
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

      <div className="max-w-3xl">
        <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-white text-xs font-semibold uppercase tracking-wide">
          Area Cliente
        </div>

        <h1 className="mt-4 text-3xl md:text-4xl font-bold leading-tight">
          Controlla i tuoi impianti in tempo reale
        </h1>

        <p className="mt-3 text-sm md:text-base text-slate-200 max-w-2xl">
          Storico interventi, documentazione tecnica e monitoraggio smart sempre disponibili.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          onClick={loadAll}
          className="px-4 py-2.5 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition"
        >
          Aggiorna
        </button>

        <button
          onClick={() => navigate('/cliente-macchine')}
          className="px-4 py-2.5 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-600 transition"
        >
          Le tue macchine
        </button>

        <button
          onClick={logout}
          className="px-4 py-2.5 bg-white/10 text-white rounded-xl font-semibold hover:bg-white/20 transition"
        >
          Logout
        </button>
      </div>

    </div>
  </div>
</section>
*/}



<div>
  <h1 className="text-2xl md:text-3xl font-bold text-slate-800">
    Area Cliente
  </h1>
  <p className="mt-1 text-sm text-slate-500">
    Storico interventi, allarmi telefonici e monitoraggio disponibili.
  </p>
</div>







        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm shadow-sm">
            {error}
          </div>
        )}

        {message && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 text-sm shadow-sm">
            {message}
          </div>
        )}

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <button
            type="button"
            onClick={() => setActiveArea('works')}
            className={`group text-left rounded-3xl border p-6 shadow-sm transition ${
              activeArea === 'works'
                ? 'border-sky-300 bg-gradient-to-br from-sky-50 to-white ring-2 ring-sky-100'
                : 'border-gray-200 bg-white hover:border-sky-200 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 pr-2">
                <p className="text-xs font-semibold uppercase tracking-wide leading-5 text-sky-700">
                  Documentazione 
                </p>

                <h2 className="mt-3 text-2xl font-bold leading-tight text-slate-800">
                  Interventi
                  <br />
                  Manutenzioni
                </h2>

                <p className="mt-2 text-sm text-slate-500 leading-6">
                  Consulta storico lavorazioni, VIT, manutenzioni e PDF generati dai tecnici.
                </p>
              </div>

<div
  className={`shrink-0 flex h-12 w-12 items-center justify-center rounded-2xl text-xl font-bold transition ${
    activeArea === 'works'
      ? 'bg-sky-600 text-white'
      : 'bg-slate-100 text-slate-500 group-hover:bg-sky-100 group-hover:text-sky-700'
  }`}
>
  →
</div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Badge className="bg-emerald-100 text-emerald-700">
                {stats.closedWorks} interventi chiusi
              </Badge>

              <Badge className="bg-sky-100 text-sky-700">
                {stats.reportsCount} report
              </Badge>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveArea('contacts')}
            className={`group text-left rounded-3xl border p-6 shadow-sm transition ${
              activeArea === 'contacts'
                ? 'border-orange-300 bg-gradient-to-br from-orange-50 to-white ring-2 ring-orange-100'
                : 'border-gray-200 bg-white hover:border-orange-200 hover:bg-slate-50'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 pr-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                  Allarmi telefonici
                </p>

                <h2 className="mt-3 text-2xl font-bold text-slate-800">
                  Contatti allarme
                </h2>

                <p className="mt-2 text-sm text-slate-500 leading-6">
                  Gestisci responsabili, reperibilità e orari lavorativi per gli allarmi.
                </p>
              </div>

              <div
                className={`shrink-0 flex h-12 w-12 items-center justify-center rounded-2xl text-xl font-bold transition ${
                  activeArea === 'contacts'
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-100 text-slate-500 group-hover:bg-orange-100 group-hover:text-orange-700'
                }`}
              >
                →
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Badge className="bg-orange-100 text-orange-700">
                {departments.length} reparti
              </Badge>

              <Badge className="bg-slate-100 text-slate-700">
                {departments.reduce(
                  (acc, department) =>
                    acc +
                    (Array.isArray(department.alarmContacts)
                      ? department.alarmContacts.filter((c) => c.isActive !== false).length
                      : 0),
                  0
                )}{' '}
                contatti
              </Badge>
              
            </div>
          </button>

<button
  type="button"
  onClick={() => setActiveArea('alarms')}
  className={`group text-left rounded-3xl border p-6 shadow-sm transition ${
    activeArea === 'alarms'
      ? 'border-red-300 bg-gradient-to-br from-red-50 to-white ring-2 ring-red-100'
      : 'border-gray-200 bg-white hover:border-red-200 hover:bg-slate-50'
  }`}
>
  <div className="flex items-start justify-between gap-4">
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
        Storico chiamate
      </p>

      <h2 className="mt-3 text-2xl font-bold text-slate-800">
        Allarmi e chiamate
      </h2>

      <p className="mt-2 text-sm text-slate-500 leading-6">
        Consulta gli allarmi telefonici e prendi in carico quelli aperti.
      </p>
    </div>

    <div
      className={`shrink-0 flex h-12 w-12 items-center justify-center rounded-2xl text-xl font-bold transition ${
        activeArea === 'alarms'
          ? 'bg-red-600 text-white'
          : 'bg-slate-100 text-slate-500 group-hover:bg-red-100 group-hover:text-red-700'
      }`}
    >
      →
    </div>
  </div>

  <div className="mt-5 flex flex-wrap gap-2">
    <Badge className="bg-orange-100 text-orange-700">
      {alarmEvents.filter((event) => eventMatchesClientVoiceFilter(event, 'problems')).length} problemi
    </Badge>

    <Badge className="bg-slate-100 text-slate-700">
      {alarmEvents.length} totali
    </Badge>
  </div>
</button>


{canShowSmartBoxes && (
  <button
    type="button"
    onClick={() => setActiveArea('smart')}
    className={`group text-left rounded-3xl border p-6 shadow-sm transition ${
      activeArea === 'smart'
        ? 'border-cyan-300 bg-gradient-to-br from-cyan-50 to-white ring-2 ring-cyan-100'
        : 'border-gray-200 bg-white hover:border-cyan-200 hover:bg-slate-50'
    }`}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
          Telemetria
        </p>

        <h2 className="mt-3 text-2xl font-bold text-slate-800">
          Dispositivi smart
        </h2>

        <p className="mt-2 text-sm text-slate-500 leading-6">
          Vedi macchine, device associati e ultimi valori delle sonde.
        </p>
      </div>

      <div
        className={`shrink-0 flex h-12 w-12 items-center justify-center rounded-2xl text-xl font-bold transition ${
          activeArea === 'smart'
            ? 'bg-cyan-600 text-white'
            : 'bg-slate-100 text-slate-500 group-hover:bg-cyan-100 group-hover:text-cyan-700'
        }`}
      >
        →
      </div>
    </div>

    <div className="mt-5 flex flex-wrap gap-2">
      <Badge className="bg-slate-100 text-slate-700">
        {smartMachinesForDisplay.length} macchine
      </Badge>

      <Badge className="bg-cyan-100 text-cyan-700">
        {smartDevicesForHistory.length} device
      </Badge>
    </div>
  </button>
)}

{canShowSmartBoxes && (
  <button
    type="button"
    onClick={() => setActiveArea('history')}
    className={`group text-left rounded-3xl border p-6 shadow-sm transition ${
      activeArea === 'history'
        ? 'border-indigo-300 bg-gradient-to-br from-indigo-50 to-white ring-2 ring-indigo-100'
        : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-slate-50'
    }`}
  >
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">
          Dati storici
        </p>

        <h2 className="mt-3 text-2xl font-bold text-slate-800">
          Storico letture
        </h2>

        <p className="mt-2 text-sm text-slate-500 leading-6">
          Consulta grafici per sonda, finestre temporali ed export CSV.
        </p>
      </div>

      <div
        className={`shrink-0 flex h-12 w-12 items-center justify-center rounded-2xl text-xl font-bold transition ${
          activeArea === 'history'
            ? 'bg-indigo-600 text-white'
            : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-700'
        }`}
      >
        →
      </div>
    </div>

    <div className="mt-5 flex flex-wrap gap-2">
      <Badge className="bg-indigo-100 text-indigo-700">
        Grafici
      </Badge>

      <Badge className="bg-emerald-100 text-emerald-700">
        CSV
      </Badge>
    </div>
  </button>
)}
        </section>
{/*
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
              Interventi chiusi
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-800">{stats.closedWorks}</p>
            <p className="mt-2 text-sm text-slate-500">
              Attività completate e visibili nel tuo storico.
            </p>
          </Card>

          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
              Report disponibili
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-800">{stats.reportsCount}</p>
            <p className="mt-2 text-sm text-slate-500">
              Verbali e documenti rigenerabili in PDF.
            </p>
          </Card>

          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
              Macchine installate
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-800">{stats.machinesCount}</p>
            <p className="mt-2 text-sm text-slate-500">
              Impianti collegati al tuo account cliente.
            </p>
          </Card>

          <Card className="p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
              Monitoraggio smart
            </p>
            <p className="mt-3 text-3xl font-bold text-slate-800">{stats.smartCount}</p>
            <p className="mt-2 text-sm text-slate-500">
              Macchine con dispositivi smart già associati.
            </p>
          </Card>
        </section>
*/}

{activeArea === 'works' && (
  <section className="grid gap-4 sm:grid-cols-2">
    <Card className="p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
        Interventi chiusi
      </p>
      <p className="mt-3 text-3xl font-bold text-slate-800">{stats.closedWorks}</p>
      <p className="mt-2 text-sm text-slate-500">
        Attività completate e visibili nel tuo storico.
      </p>
    </Card>

    <Card className="p-5 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">
        Report disponibili
      </p>
      <p className="mt-3 text-3xl font-bold text-slate-800">{stats.reportsCount}</p>
      <p className="mt-2 text-sm text-slate-500">
        Verbali e documenti rigenerabili in PDF.
      </p>
    </Card>
  </section>
)}


{activeArea === 'alarms' && (
  <section className="rounded-3xl border border-gray-200 bg-white py-5 md:p-6 shadow-sm overflow-hidden">
    <div className="px-4 md:px-0 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
      <div>
        <h2 className="text-xl font-bold text-slate-800">
          Allarmi e storico chiamate
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Qui trovi gli allarmi telefonici collegati ai tuoi impianti e lo storico dei tentativi.
        </p>
      </div>

      <button
        type="button"
        onClick={async () => {
          const refreshed = await loadAlarmEvents();
          setAlarmEvents(refreshed);
        }}
        disabled={loadingAlarmEvents}
        className="px-4 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 transition disabled:opacity-60"
      >
        {loadingAlarmEvents ? 'Aggiorno...' : 'Aggiorna'}
      </button>
    </div>

    <div className="mt-5 px-4 md:px-0 space-y-3">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-3 py-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Stato allarme
            </p>
            <p className="text-xs text-slate-400">
              Filtra gli eventi per stato operativo.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {CLIENT_ALARM_STATUS_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setAlarmStatusFilter(filter.value)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold transition ${
                  alarmStatusFilter === filter.value
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="h-px bg-slate-200" />

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Stato chiamata
            </p>
            <p className="text-xs text-slate-400">
              Isola chiamate non avviate, in corso, confermate o problematiche.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {CLIENT_VOICE_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setAlarmVoiceFilter(filter.value)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold transition ${
                  alarmVoiceFilter === filter.value
                    ? 'bg-indigo-700 text-white'
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <input
        value={alarmQuery}
        onChange={(e) => setAlarmQuery(e.target.value)}
        placeholder="Cerca per macchina, reparto, sonda, numero chiamato..."
        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
      />

      <div className="flex flex-wrap gap-2">
        <Badge className="bg-red-100 text-red-700">
          {filteredAlarmEvents.length} eventi visualizzati
        </Badge>

        <Badge className="bg-slate-100 text-slate-700">
          {alarmEvents.length} eventi totali
        </Badge>
      </div>
    </div>
    
    <div className="mt-6 px-4 md:px-0">
      {filteredAlarmEvents.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm text-gray-500">
            Nessun allarme telefonico disponibile per i filtri selezionati.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredAlarmEvents.map((event) => {
            const attempts = getSortedVoiceAttempts(event);
            const canAck =
              event.status === 'active' &&
              event.voice_acknowledged !== true;

            return (
              <div
                key={event._id}
                className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 sm:p-5 shadow-sm"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap gap-2">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${getAlarmStatusClass(event.status)}`}>
                        {getAlarmStatusLabel(event.status)}
                      </span>

                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${getVoiceStatusClass(event.voice_call_status)}`}>
                        Chiamata: {getVoiceStatusLabel(event.voice_call_status)}
                      </span>
                    </div>

                    <h3 className="mt-3 text-base sm:text-lg font-bold text-slate-800">
                      {event.sensor_label || event.sensor_id || 'Sensore'}
                    </h3>

                    <p className="mt-1 text-sm text-slate-500">
                      {event.machineRef?.nome || 'Macchina non specificata'}
                      {event.departmentRef?.name ? ` · ${event.departmentRef.name}` : ''}
                    </p>

                    <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                      <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">
                          Valore
                        </p>
                        <p className="mt-1 font-semibold text-slate-800">
                          {event.value ?? '-'} {event.unit || 'C'}
                        </p>
                      </div>

                      <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">
                          Soglie
                        </p>
                        <p className="mt-1 font-semibold text-slate-800">
                          min {event.min_value ?? '-'} / max {event.max_value ?? '-'}
                        </p>
                      </div>

                      <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">
                          Scattato
                        </p>
                        <p className="mt-1 font-semibold text-slate-800">
                          {formatDateTime(event.triggered_at)}
                        </p>
                      </div>

                      <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">
                          Tentativi
                        </p>
                        <p className="mt-1 font-semibold text-slate-800">
                          {Number(event.voice_call_attempts || 0)}
                        </p>
                      </div>
                    </div>

                    {event.voice_last_error && (
                      <p className="mt-3 rounded-xl bg-orange-50 border border-orange-100 px-3 py-2 text-xs text-orange-700">
                        {event.voice_last_error}
                      </p>
                    )}
                  </div>

                  <div className="shrink-0">
                    {canAck ? (
                      <button
                        type="button"
                        onClick={() => handleAcknowledgeAlarm(event)}
                        disabled={ackingAlarmId === event._id}
                        className="w-full lg:w-auto px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-60"
                      >
                        {ackingAlarmId === event._id
                          ? 'Presa in carico...'
                          : 'Prendi in carico'}
                      </button>
                    ) : (
                      <span className="inline-flex items-center px-3 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-semibold">
                        Nessuna azione richiesta
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-bold text-slate-800">
                      Storico chiamate
                    </h4>

                    <span className="text-xs text-slate-400">
                      {attempts.length} tentativi
                    </span>
                  </div>

                  {attempts.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">
                      Nessun tentativo telefonico registrato.
                    </p>
                  ) : (
                    <div className="mt-3 grid gap-2">
                      {attempts.map((attempt, index) => (
                        <div
                          key={attempt._id || attempt.call_sid || index}
                          className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-slate-800">
                                {attempt.contact_name || 'Contatto'}
                              </p>

                              <p className="mt-1 text-xs text-slate-500">
                                {attempt.phone || event.voice_call_to || '-'}
                              </p>
                            </div>

                            <span className={`inline-flex w-fit items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${getVoiceStatusClass(attempt.status)}`}>
                              {getVoiceStatusLabel(attempt.status)}
                            </span>
                          </div>

                          <div className="mt-2 grid gap-1 text-xs text-slate-500 sm:grid-cols-3">
                            <p>
                              Avvio: {formatDateTime(attempt.started_at)}
                            </p>
                            <p>
                              Risposta: {formatDateTime(attempt.answered_at)}
                            </p>
                            <p>
                              Fine: {formatDateTime(attempt.completed_at)}
                            </p>
                          </div>

                          {attempt.error && (
                            <p className="mt-2 text-xs text-orange-700">
                              {attempt.error}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </section>
)}



{activeArea === 'contacts' && (
  <section className="rounded-3xl border border-gray-200 bg-white py-5 md:p-6 shadow-sm overflow-hidden">
    <div className="px-4 md:px-0 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
      <div>
        <h2 className="text-xl font-bold text-slate-800">
          Contatti allarme telefonico
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Configura chi deve essere chiamato in caso di allarme e quando è reperibile.
        </p>

      </div>

      <select
        value={selectedDepartmentInfo?._id || ''}
        onChange={(e) => {
          setSelectedDepartmentId(e.target.value);
          resetAlarmContactForm();
          setMessage('');
          setError('');
        }}
        className="w-full lg:w-[360px] px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
      >
        {departments.length === 0 ? (
          <option value="">Nessun reparto disponibile</option>
        ) : (
          departments.map((department) => (
            <option key={department._id} value={department._id}>
              {department.name || 'Reparto'}
              {department.sede ? ` - ${department.sede}` : ''}
            </option>
          ))
        )}
      </select>
    </div>

    {!selectedDepartmentInfo?._id ? (
      <div className="mt-6 rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
        <p className="text-sm text-gray-500">
          Nessun reparto disponibile. Contatta Ciesse per configurare i reparti.
        </p>
      </div>
    ) : (
      <div className="mt-6 grid gap-6 xl:grid-cols-[0.95fr_1.25fr]">
        <div className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4 sm:p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                Orario lavorativo
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                I contatti “Solo orario lavorativo” vengono chiamati solo dentro queste fasce.
              </p>
            </div>
          </div>


          <div className="mt-4 space-y-3">
            {WEEK_DAYS.map((day) => {
              const value = workingHoursForm[day.key] || DEFAULT_WORKING_HOURS[day.key];

              return (
                <div
                  key={day.key}
                  className="rounded-xl border border-orange-100 bg-white p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <input
                        type="checkbox"
                        checked={value.enabled !== false}
                        onChange={(e) =>
                          handleWorkingHoursChange(day.key, 'enabled', e.target.checked)
                        }
                        className="h-4 w-4"
                      />
                      {day.label}
                    </label>

                    <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                      value.enabled !== false
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      {value.enabled !== false ? 'Aperto' : 'Chiuso'}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <input
                      type="time"
                      value={value.from || '08:00'}
                      disabled={value.enabled === false}
                      onChange={(e) =>
                        handleWorkingHoursChange(day.key, 'from', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white disabled:bg-gray-100"
                    />

                    <input
                      type="time"
                      value={value.to || '18:00'}
                      disabled={value.enabled === false}
                      onChange={(e) =>
                        handleWorkingHoursChange(day.key, 'to', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white disabled:bg-gray-100"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleSubmitWorkingHours}
            disabled={savingWorkingHours}
            className="mt-4 w-full px-4 py-2.5 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 transition disabled:opacity-60"
          >
            {savingWorkingHours ? 'Salvataggio...' : 'Salva orario lavorativo'}
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                Responsabili reparto
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                Il sistema chiama prima i responsabili sempre reperibili, poi quelli disponibili solo in orario lavorativo.
              </p>

              <p className="mt-2 text-xs font-semibold text-orange-700">
                Reparto selezionato: {selectedDepartmentInfo.name || '-'}
              </p>
            </div>

            {editingAlarmContactId && (
              <button
                type="button"
                onClick={resetAlarmContactForm}
                className="px-3 py-2 rounded-xl bg-gray-200 text-gray-800 text-xs font-semibold hover:bg-gray-300 transition"
              >
                Annulla modifica
              </button>
            )}
          </div>

          <form
            onSubmit={handleSubmitAlarmContact}
            className="mt-5 grid gap-3 md:grid-cols-2"
          >
            <input
              type="text"
              name="firstName"
              value={alarmContactForm.firstName}
              onChange={handleAlarmContactChange}
              placeholder="Nome"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
              required
            />

            <input
              type="text"
              name="lastName"
              value={alarmContactForm.lastName}
              onChange={handleAlarmContactChange}
              placeholder="Cognome"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
              required
            />

            <input
              type="tel"
              name="phone"
              value={alarmContactForm.phone}
              onChange={handleAlarmContactChange}
              placeholder="Telefono es. +393..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
              required
            />

            <select
              name="availabilityMode"
              value={alarmContactForm.availabilityMode}
              onChange={handleAlarmContactChange}
              className="md:col-span-2 w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
            >
              {CONTACT_AVAILABILITY_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>

            <textarea
              name="notes"
              value={alarmContactForm.notes}
              onChange={handleAlarmContactChange}
              placeholder="Note opzionali"
              rows={2}
              className="md:col-span-2 w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
            />

            <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                name="voiceEnabled"
                checked={alarmContactForm.voiceEnabled}
                onChange={handleAlarmContactChange}
                className="h-4 w-4"
              />
              Abilitato alle chiamate automatiche
            </label>

            <button
              type="submit"
              disabled={savingAlarmContact}
              className="md:col-span-2 px-4 py-2.5 rounded-xl bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 transition disabled:opacity-60"
            >
              {savingAlarmContact
                ? 'Salvataggio...'
                : editingAlarmContactId
                ? 'Salva modifica'
                : 'Aggiungi responsabile'}
            </button>
          </form>

          <div className="mt-5">
            {selectedAlarmContacts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-center">
                <p className="text-sm text-gray-500">
                  Nessun contatto allarme inserito per questo reparto.
                </p>
              </div>
            ) : (
              <div className="grid gap-3">
              {selectedAlarmContacts
                .slice()
                .sort((a, b) => {
                  const aAlways = a.availabilityMode !== 'business_hours';
                  const bAlways = b.availabilityMode !== 'business_hours';

                  if (aAlways === bAlways) return 0;

                  return aAlways ? -1 : 1;
                })
                .map((contact) => {
                    const availabilityLabel =
                      contact.availabilityMode === 'business_hours'
                        ? 'Solo orario lavorativo'
                        : 'Sempre reperibile';

                    return (
                      <div
                        key={contact._id}
                        className="rounded-xl border border-gray-200 bg-white p-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div>
                            <h4 className="text-sm font-bold text-slate-800">
                              {contact.firstName} {contact.lastName}
                            </h4>

                            <p className="mt-1 text-sm text-slate-600">
                              {contact.phone}
                            </p>

                            <div className="mt-3 flex flex-wrap gap-2">
                              <Badge className="bg-orange-100 text-orange-700">
                                {availabilityLabel}
                              </Badge>

                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                                contact.voiceEnabled === false
                                  ? 'bg-slate-100 text-slate-500'
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                {contact.voiceEnabled === false ? 'Disabilitato' : 'Attivo'}
                              </span>
                            </div>

                            {contact.notes && (
                              <p className="mt-3 text-xs text-slate-500">
                                {contact.notes}
                              </p>
                            )}
                          </div>

                          <div className="flex sm:flex-col gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditAlarmContact(contact)}
                              className="px-3 py-2 rounded-lg bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 transition"
                            >
                              Modifica
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteAlarmContact(contact)}
                              className="px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition"
                            >
                              Disattiva
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </section>
)}

{canShowSmartBoxes && activeArea === 'smart' && (
  <section className="space-y-6">
    <div className="rounded-3xl border border-gray-200 bg-white py-5 md:p-6 shadow-sm overflow-hidden">
      <div className="px-4 md:px-0 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">
            Telemetria
          </p>
          <h2 className="mt-1 text-xl font-bold text-slate-800">
            Dispositivi smart
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Macchine associate al tuo account, device installati e ultimi valori disponibili.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
            {smartMachinesForDisplay.length} macchine
          </span>
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-cyan-100 text-cyan-700">
            {smartDevicesForHistory.length} device
          </span>
        </div>
      </div>

      {smartMachinesForDisplay.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
          <p className="text-sm text-gray-500">
            Nessuna macchina associata al tuo account al momento.
          </p>
        </div>
      ) : (
        <div className="mt-6 grid gap-4 xl:grid-cols-2">
          {smartMachinesForDisplay.map((machine, machineIndex) => {
            const devices = Array.isArray(machine.devices) ? machine.devices : [];
            const latestSeen = devices
              .map((device) => device?.last_seen_at)
              .filter(Boolean)
              .sort()
              .at(-1);

            return (
              <article
                key={machine._id || machine.id || machine.matricola || machineIndex}
                className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 shadow-sm"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-slate-800 break-words">
                      {machine.nome || machine.name || 'Macchina'}
                    </h3>

                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge className="bg-slate-100 text-slate-700 max-w-full break-all">
                        {machine.matricola || machine.serialNumber || 'Senza matricola'}
                      </Badge>
                      {renderSmartBadge(devices)}
                    </div>
                  </div>

                  <div className="shrink-0 sm:text-right">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">
                      Device
                    </p>
                    <p className="mt-1 text-2xl font-bold text-slate-800">
                      {devices.length}
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm">
                  <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">
                      Sede
                    </p>
                    <p className="mt-1 text-slate-700 break-words">
                      {machine.sede || machine.location || machine.departmentRef?.sede || '-'}
                    </p>
                  </div>

                  <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">
                      Reparto
                    </p>
                    <p className="mt-1 text-slate-700 break-words">
                      {machine.reparto || machine.departmentRef?.name || '-'}
                    </p>
                  </div>
                </div>

                <div className="mt-3 rounded-xl bg-white border border-slate-200 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">
                    Ultimo contatto
                  </p>
                  <p className="mt-1 text-sm text-slate-700 break-words">
                    {formatDateTime(latestSeen)}
                  </p>
                </div>

                {devices.length === 0 ? (
                  <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white px-3 py-4 text-center">
                    <p className="text-sm text-slate-500">
                      Nessun dispositivo smart associato a questa macchina.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    {devices.map((device, deviceIndex) => {
                      const channels = getDeviceChannels(device);

                      return (
                        <div
                          key={getDeviceDisplayId(device) || deviceIndex}
                          className="rounded-xl border border-cyan-100 bg-cyan-50/40 p-3"
                        >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-cyan-700 font-semibold">
                                Device
                              </p>
                              <p className="mt-1 text-sm font-bold text-slate-800 break-all">
                                {getDeviceDisplayId(device)}
                              </p>
                            </div>

                            <span className="inline-flex w-fit items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white text-slate-600 border border-cyan-100">
                              {channels.length} sonde
                            </span>
                          </div>

                          {channels.length === 0 ? (
                            <p className="mt-3 text-sm text-slate-500">
                              Nessuna sonda rilevata per questo device.
                            </p>
                          ) : (
                            <div className="mt-3 grid gap-2 sm:grid-cols-2">
                              {channels.map((channel, channelIndex) => (
                                <div
                                  key={
                                    channel?.sensor_id ||
                                    channel?.id ||
                                    `${getDeviceDisplayId(device)}-${channelIndex}`
                                  }
                                  className="rounded-lg bg-white border border-cyan-100 px-3 py-2"
                                >
                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                    {getChannelLabel(channel)}
                                  </p>
                                  <p className="mt-1 text-base font-bold text-slate-800">
                                    {getChannelValueLabel(channel)}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  </section>
)}

{canShowSmartBoxes && activeArea === 'history' && (
  <section className="space-y-6">
    <StoricoLetture
      embedded
      devicesOverride={smartDevicesForHistory}
      onBack={() => setActiveArea('works')}
    />
  </section>
)}

{/*       <section className="grid gap-4 xl:gap-6 xl:grid-cols-[1.05fr_1.35fr]">  */}
{activeArea === 'works' && (
<section>

{/*
          <div className="rounded-3xl border border-gray-200 bg-white py-5 md:p-6 shadow-sm overflow-hidden">
            <div className="mb-4 px-4 md:px-0">
              <h2 className="text-xl font-bold text-slate-800">
                Le tue macchine
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Elenco impianti installati e stato del monitoraggio smart.
              </p>
            </div>

            {machines.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center">
                <p className="text-sm text-gray-500">
                  Nessuna macchina associata al tuo account al momento.
                </p>
              </div>
            ) : (
              <div className="space-y-3 px-2 md:px-0">
                {machines.map((machine) => {
                  const devices = Array.isArray(machine.devices) ? machine.devices : [];
                  const latestSeen = devices[0]?.last_seen_at || null;

                  return (
                  <div
                    key={machine._id}
                    className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-3 shadow-sm"
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="text-base md:text-lg font-semibold text-slate-800 break-words">
                            {machine.nome || 'Macchina'}
                          </h3>

                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge className="bg-slate-100 text-slate-700 max-w-full break-all">
                              {machine.matricola || 'Senza matricola'}
                            </Badge>
                            {renderSmartBadge(devices)}
                          </div>
                        </div>

                        <div className="shrink-0 text-right">
                          <p className="text-[10px] uppercase tracking-wide text-slate-400">
                            Device
                          </p>
                          <p className="mt-1 text-xl md:text-2xl font-bold text-slate-800">
                            {devices.length}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-2.5 text-sm">
                        <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-slate-400">Sede</p>
                          <p className="mt-1 text-slate-700 break-words">{machine.sede || '-'}</p>
                        </div>

                        <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-slate-400">Reparto</p>
                          <p className="mt-1 text-slate-700 break-words">{machine.reparto || '-'}</p>
                        </div>
                      </div>

                      <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2">
                        <p className="text-[11px] uppercase tracking-wide text-slate-400">Ultimo contatto</p>
                        <p className="mt-1 text-sm text-slate-700 break-words">
                          {formatDateTime(latestSeen)}
                        </p>
                      </div>

                      {devices.length > 0 && (
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">
                            Device associati
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {devices.slice(0, 3).map((device) => (
                              <span
                                key={device._id}
                                className="px-2.5 py-1 text-[11px] rounded-full bg-sky-100 text-sky-700 font-medium border border-sky-200 max-w-full break-all"
                              >
                                {device.device_id}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {machine.note && (
                        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
                          <p className="text-[11px] uppercase tracking-wide text-amber-700">Note</p>
                          <p className="mt-1 text-sm text-slate-700 break-words">{machine.note}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
*/}




          <div className="rounded-3xl border border-gray-200 bg-white py-5 md:p-6 shadow-sm overflow-hidden">
            <div className="px-4 md:px-0 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  Interventi e manutenzioni
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Consulta lo storico delle attività collegate al tuo account.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full lg:w-auto lg:min-w-[420px]">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca per codice, azienda, luogo..."
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                />

                <select
                  value={statusMode}
                  onChange={(e) => setStatusMode(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                >
                  <option value="closed">Solo eseguite</option>
                  <option value="all">Tutte</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              {filteredRows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center">
                  <p className="text-sm text-gray-500">
                    Nessuna lavorazione disponibile per i filtri selezionati.
                  </p>
                </div>
              ) : (
            <>

            {/* MOBILE */}
            <div className="md:hidden mx-2 overflow-hidden rounded-2xl border border-gray-200">
              <table className="min-w-full border-collapse text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide">
                  <tr>
                    <th className="border-b border-gray-200 px-2 py-3 text-left">Codice</th>
                    <th className="border-b border-gray-200 px-2 py-3 text-left">Data</th>
                    <th className="border-b border-gray-200 px-2 py-3 text-left">Stato</th>
                    <th className="border-b border-gray-200 px-2 py-3 text-left">Report</th>
                    <th className="border-b border-gray-200 px-2 py-3 text-right">PDF</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRows.map((row) => {
                    const w = row?.workOrder || {};
                    const reports = row?.reports || [];

                    const hasReports = reports.length > 0;
                    const canDownloadPdf =
                      w.status === 'chiusa' ||
                      w.status === 'completata' ||
                      w.assignedReportCompiled === true ||
                      hasReports;

                    return (
                      <tr key={w._id} className="hover:bg-slate-50 transition-colors align-top">
                        <td className="border-b border-gray-100 px-2 py-3">
                          <div className="font-semibold text-slate-800 break-all">
                            {w.internalCode || '-'}
                          </div>
                          {w.externalCode && (
                            <div className="mt-1 text-[10px] text-slate-400 break-all">
                              {w.externalCode}
                            </div>
                          )}
                        </td>

                        <td className="border-b border-gray-100 px-2 py-3 whitespace-nowrap">
                          {formatDate(w.receivedAt)}
                        </td>

                        <td className="border-b border-gray-100 px-2 py-3">
                          {renderStatusBadge(w.status)}
                        </td>

                        <td className="border-b border-gray-100 px-2 py-3">
                          {renderReportsBadge(reports)}
                        </td>

                        <td className="border-b border-gray-100 px-2 py-3 text-right">
                          {canDownloadPdf ? (
                            <button
                              type="button"
                              disabled={pdfLoadingId === w._id}
                              onClick={() => handleRegeneratePdf(w._id)}
                              className={`px-2.5 py-2 rounded-lg text-[11px] font-semibold transition ${
                                pdfLoadingId === w._id
                                  ? 'bg-slate-300 text-white'
                                  : 'bg-slate-800 text-white hover:bg-slate-900'
                              }`}
                            >
                              {pdfLoadingId === w._id ? '...' : 'PDF'}
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

              {/* DESKTOP / TABLET */}
              <div className="hidden md:block overflow-auto max-h-[620px] rounded-2xl border border-gray-200">
                <table className="min-w-full border-collapse text-xs lg:text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                    <tr className="hover:bg-slate-50 transition">
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Codice interno</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Codice Ciesse</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Data</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Tipo</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Azienda</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Luogo</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Stato</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Report</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-left">Descrizione</th>
                      <th className="border-b border-gray-200 px-3 py-3 text-right">PDF</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((row) => {
                      const w = row?.workOrder || {};
                      const reports = row?.reports || [];

                      const hasReports = reports.length > 0;
                      const canDownloadPdf =
                        w.status === 'chiusa' ||
                        w.status === 'completata' ||
                        w.assignedReportCompiled === true ||
                        hasReports;

                      return (
                        <tr key={w._id} className="hover:bg-slate-50 transition-colors">
                          <td className="border-b border-gray-100 px-3 py-3">
                            {w.externalCode || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3">
                            {w.internalCode || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3">
                            {formatDate(w.receivedAt)}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3">
                            {w.type || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3">
                            {w.companyName || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3">
                            {w.place || '-'}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3">
                            {renderStatusBadge(w.status)}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3">
                            {renderReportsBadge(reports)}
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3">
                            <span className="block max-w-[320px] lg:max-w-[420px] truncate">
                              {w.description || '-'}
                            </span>
                          </td>
                          <td className="border-b border-gray-100 px-3 py-3 text-right">
                            {canDownloadPdf ? (
                              <button
                                type="button"
                                disabled={pdfLoadingId === w._id}
                                onClick={() => handleRegeneratePdf(w._id)}
                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition ${
                                  pdfLoadingId === w._id
                                    ? 'bg-slate-300 text-white'
                                    : 'bg-slate-800 text-white hover:bg-slate-900'
                                }`}
                              >
                                {pdfLoadingId === w._id ? 'Carico…' : 'Rigenera PDF'}
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
              )}
            </div>
          </div>
        </section>
        )}






        
      </div>

      <WhatsAppFloatingButton
        phoneNumber={WHATSAPP_BUSINESS_NUMBER}
        helperText="Serve aiuto sul servizio?"
        label="Apri supporto WhatsApp Ciesse"
        message="Ciao, sono un cliente Ciesse. Ho bisogno di supporto sul servizio di monitoraggio e sugli allarmi."
        sideKey="ciesse_dashboard_cliente_whatsapp_float_side"
      />
    </div>
  );
}